// Milestone celebration. Fires brand-palette confetti + announces the
// moment to screen readers. Respects prefers-reduced-motion (no confetti,
// just the announcement). Use sparingly — once per real win — so it
// stays delightful instead of noisy.
//
// Wiring sites (V1):
//   - First medication ever added (Medications + Welcome flows)
//   - First check-off streak threshold reached (Pillbox, 3 / 7 / 30 days)
//   - All safety flags resolved (Interactions, after recompute)

import confetti from 'canvas-confetti';
import { useCallback } from 'react';

// Brand palette + warm complements. Confetti pulls from these in order.
const BRAND_COLORS = ['#0F4C75', '#1F7A3E', '#E3A04A', '#FAF7F2', '#D9E7F3'];

function announce(text: string): void {
  if (typeof document === 'undefined') return;
  // One reused aria-live region. SR users hear the milestone without
  // visual flair. Re-read on every fire by resetting textContent.
  let region = document.getElementById('celebrate-live');
  if (!region) {
    region = document.createElement('div');
    region.id = 'celebrate-live';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.style.position = 'absolute';
    region.style.left = '-10000px';
    region.style.width = '1px';
    region.style.height = '1px';
    region.style.overflow = 'hidden';
    document.body.appendChild(region);
  }
  region.textContent = '';
  // Slight delay so SR re-reads if the same text is fired twice in a row.
  setTimeout(() => {
    if (region) region.textContent = text;
  }, 50);
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export type CelebrateKind = 'first-med' | 'streak' | 'flag-resolved' | 'generic';

const ANNOUNCEMENTS: Record<CelebrateKind, string> = {
  'first-med': 'First medicine added. Nice work.',
  streak: 'You hit a new check-off streak. Beautiful, keep going.',
  'flag-resolved': 'All your safety flags are cleared. Great work.',
  generic: 'Milestone reached.',
};

export function useCelebrate(): (kind?: CelebrateKind, custom?: string) => void {
  return useCallback((kind: CelebrateKind = 'generic', custom?: string) => {
    const text = custom ?? ANNOUNCEMENTS[kind];
    announce(text);
    if (prefersReducedMotion()) return;
    // Two-burst confetti from bottom corners — easier on senior eyes
    // than a single full-screen blast.
    const opts = {
      particleCount: 70,
      spread: 65,
      startVelocity: 45,
      gravity: 0.9,
      ticks: 220,
      colors: BRAND_COLORS,
      scalar: 1.1,
    };
    confetti({ ...opts, angle: 60, origin: { x: 0, y: 1 } });
    confetti({ ...opts, angle: 120, origin: { x: 1, y: 1 } });
  }, []);
}
